import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Trophy, Timer, Target, Crosshair } from 'lucide-react';

const TargetHitAccuracy = ({ onGameEnd }) => {
  const canvasRef = useRef(null);
  const [gameState, setGameState] = useState('waiting');
  const [score, setScore] = useState(0);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [targets, setTargets] = useState([]);
  const [combo, setCombo] = useState(0);
  const gameLoopRef = useRef(null);

  const createTarget = (width, height) => {
    const size = 40 + Math.random() * 40;
    return {
      x: size + Math.random() * (width - size * 2),
      y: size + Math.random() * (height - size * 2),
      size,
      speed: 1 + Math.random() * 2,
      dx: (Math.random() - 0.5) * 3,
      dy: (Math.random() - 0.5) * 3,
      color: `hsl(${Math.random() * 360}, 70%, 50%)`,
      id: Date.now() + Math.random()
    };
  };

  useEffect(() => {
    if (gameState === 'playing') {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // Initialize targets
      if (targets.length === 0) {
        const initialTargets = [];
        for (let i = 0; i < 5; i++) {
          initialTargets.push(createTarget(width, height));
        }
        setTargets(initialTargets);
      }

      const gameLoop = () => {
        // Clear canvas
        ctx.fillStyle = '#0a0a0a';
        ctx.fillRect(0, 0, width, height);

        // Draw grid background
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
        ctx.lineWidth = 1;
        for (let i = 0; i < width; i += 50) {
          ctx.beginPath();
          ctx.moveTo(i, 0);
          ctx.lineTo(i, height);
          ctx.stroke();
        }
        for (let i = 0; i < height; i += 50) {
          ctx.beginPath();
          ctx.moveTo(0, i);
          ctx.lineTo(width, i);
          ctx.stroke();
        }

        // Update and draw targets
        setTargets(prevTargets => {
          return prevTargets.map(target => {
            // Move target
            target.x += target.dx * target.speed;
            target.y += target.dy * target.speed;

            // Bounce off walls
            if (target.x - target.size < 0 || target.x + target.size > width) {
              target.dx *= -1;
              target.x = Math.max(target.size, Math.min(width - target.size, target.x));
            }
            if (target.y - target.size < 0 || target.y + target.size > height) {
              target.dy *= -1;
              target.y = Math.max(target.size, Math.min(height - target.size, target.y));
            }

            // Draw target circles
            for (let i = 3; i >= 0; i--) {
              const radius = target.size * (1 - i * 0.2);
              ctx.fillStyle = i % 2 === 0 ? target.color : '#fff';
              ctx.beginPath();
              ctx.arc(target.x, target.y, radius, 0, Math.PI * 2);
              ctx.fill();
            }

            // Draw center dot
            ctx.fillStyle = '#ff0000';
            ctx.beginPath();
            ctx.arc(target.x, target.y, 5, 0, Math.PI * 2);
            ctx.fill();

            return target;
          });
        });

        gameLoopRef.current = requestAnimationFrame(gameLoop);
      };

      gameLoop();

      return () => {
        if (gameLoopRef.current) {
          cancelAnimationFrame(gameLoopRef.current);
        }
      };
    }
  }, [gameState, targets.length]);

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('finished');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  useEffect(() => {
    if (gameState === 'finished') {
      // Stop the game loop
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
      // Call onGameEnd after a short delay to show the final screen
      setTimeout(() => {
        const accuracy = hits > 0 ? Math.round((hits / (hits + misses)) * 100) : 0;
        onGameEnd(score, accuracy);
      }, 2000);
    }
  }, [gameState, score, hits, misses, onGameEnd]);

  const handleCanvasClick = (e) => {
    if (gameState !== 'playing') return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    let hitTarget = false;
    let hitIndex = -1;

    targets.forEach((target, index) => {
      const distance = Math.sqrt(
        Math.pow(x - target.x, 2) + Math.pow(y - target.y, 2)
      );

      if (distance <= target.size) {
        hitTarget = true;
        hitIndex = index;

        // Calculate points based on accuracy
        const accuracy = 1 - (distance / target.size);
        const basePoints = Math.round(accuracy * 100);
        const comboBonus = combo * 10;
        const totalPoints = basePoints + comboBonus;

        setScore(prev => prev + totalPoints);
        setHits(prev => prev + 1);
        setCombo(prev => prev + 1);
      }
    });

    if (hitTarget) {
      // Remove hit target and create new one
      const canvas = canvasRef.current;
      const newTarget = createTarget(canvas.width, canvas.height);
      setTargets(prev => {
        const updated = [...prev];
        updated.splice(hitIndex, 1);
        updated.push(newTarget);
        return updated;
      });
    } else {
      setMisses(prev => prev + 1);
      setCombo(0);
    }
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setHits(0);
    setMisses(0);
    setTimeLeft(60);
    setTargets([]);
    setCombo(0);
  };

  const accuracy = hits + misses > 0 ? Math.round((hits / (hits + misses)) * 100) : 0;

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="bg-gradient-to-br from-red-900/30 to-orange-900/30 rounded-lg p-6 border border-red-500/30">
        {/* Game Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-black/30 rounded p-2 text-center">
            <Trophy className="w-4 h-4 mx-auto text-yellow-400 mb-1" />
            <div className="text-xl font-bold text-white">{score}</div>
            <div className="text-xs text-gray-400">Score</div>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <Timer className="w-4 h-4 mx-auto text-blue-400 mb-1" />
            <div className="text-xl font-bold text-white">{timeLeft}s</div>
            <div className="text-xs text-gray-400">Time</div>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <Target className="w-4 h-4 mx-auto text-green-400 mb-1" />
            <div className="text-xl font-bold text-white">{hits}</div>
            <div className="text-xs text-gray-400">Hits</div>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <Crosshair className="w-4 h-4 mx-auto text-purple-400 mb-1" />
            <div className="text-xl font-bold text-white">{accuracy}%</div>
            <div className="text-xs text-gray-400">Accuracy</div>
          </div>
        </div>

        {combo > 1 && gameState === 'playing' && (
          <div className="text-center mb-2">
            <span className="text-yellow-400 font-bold text-lg animate-pulse">
              🔥 COMBO x{combo}!
            </span>
          </div>
        )}

        {/* Game Canvas */}
        <div className="relative bg-black rounded-lg overflow-hidden mb-4">
          <canvas
            ref={canvasRef}
            width={600}
            height={500}
            className="w-full h-auto cursor-crosshair"
            onClick={handleCanvasClick}
          />
          
          {gameState === 'waiting' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
              <Crosshair className="w-16 h-16 text-red-400 mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Target Hit Accuracy</h3>
              <p className="text-gray-300 text-center mb-4 max-w-md px-4">
                Click the moving targets as accurately as possible!
                Hit the center for maximum points. Build combos for bonus points!
              </p>
              <Button onClick={startGame} className="gaming-button">
                Start Game
              </Button>
            </div>
          )}

          {gameState === 'finished' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90">
              <Trophy className="w-20 h-20 text-yellow-400 mb-4" />
              <h3 className="text-3xl font-bold text-white mb-2">Game Over!</h3>
              <div className="text-center mb-6">
                <p className="text-2xl text-yellow-400 font-bold">Score: {score}</p>
                <p className="text-xl text-green-400">Hits: {hits}</p>
                <p className="text-xl text-blue-400">Accuracy: {accuracy}%</p>
              </div>
              <p className="text-gray-300">Submitting your score...</p>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="text-sm text-gray-400 text-center">
          <p>🎯 Click the center of targets for maximum points!</p>
          <p>🔥 Hit multiple targets in a row to build a combo!</p>
        </div>
      </div>
    </div>
  );
};

export default TargetHitAccuracy;
